import {
  Activity,
  BarChart3,
  BookOpen,
  Building2,
  ChevronDown,
  ChevronRight,
  CreditCard,
  DollarSign,
  HelpCircle,
  LayoutDashboard,
  LogOut,
  Rocket,
  Megaphone,
  Percent,
  Shield,
  Stethoscope,
  ToggleLeft,
  ToggleRight,
  UserCog,
  UserPlus,
  Users,
  Wrench,
} from "@ews-admin/global-design-system";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { scrollToTop } from "../../hooks/useScrollToTop";
import { useAppSelector } from "../../store/hooks";
import { requestTerminateSessionRequest } from "../../store/reducers/sessionSlice";
import { roleLabel } from "../../utils/enum-label-utils";
import { LanguageSwitcher } from "../molecules/LanguageSwitcher";

interface MenuLeaf {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
}

interface MenuGroup {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  children: MenuLeaf[];
}

type MenuEntry = MenuLeaf | MenuGroup;

function isGroup(entry: MenuEntry): entry is MenuGroup {
  return (entry as MenuGroup).children !== undefined;
}

interface SidebarProps {
  onClose?: () => void;
  isMobile?: boolean;
}

const adminMenuItems: MenuEntry[] = [
  {
    id: "admin-dashboard",
    label: "admin.tabs.overview",
    icon: LayoutDashboard,
    path: "/admin",
  },
  {
    id: "admin-onboarding",
    label: "admin.tabs.onboarding",
    icon: Rocket,
    path: "/admin/onboarding",
  },
  {
    id: "admin-facilities",
    label: "admin.tabs.facilities",
    icon: Building2,
    path: "/admin/facilities",
  },
  {
    id: "admin-managers",
    label: "admin.tabs.managers",
    icon: UserPlus,
    path: "/admin/managers",
  },
  {
    id: "admin-doctors",
    label: "admin.tabs.doctors",
    icon: UserCog,
    path: "/admin/doctors",
  },
  {
    id: "admin-users",
    label: "admin.tabs.users",
    icon: Users,
    path: "/admin/users",
  },
  {
    id: "admin-invoices",
    label: "admin.tabs.invoices",
    icon: CreditCard,
    path: "/admin/invoices",
  },
  {
    id: "admin-features",
    label: "admin.tabs.features",
    icon: ToggleRight,
    path: "/admin/features",
  },
  {
    id: "admin-analytics",
    label: "admin.tabs.analytics",
    icon: BarChart3,
    path: "/admin/analytics",
  },
  {
    id: "admin-faq",
    label: "admin.tabs.faq",
    icon: BookOpen,
    path: "/admin/faq",
  },
  {
    id: "admin-specialties",
    label: "admin.tabs.specialties",
    icon: Stethoscope,
    path: "/admin/specialties",
  },
  {
    id: "admin-care-types",
    label: "admin.tabs.careTypes",
    icon: Activity,
    path: "/admin/care-types",
  },
  {
    id: "admin-pricing",
    label: "admin.tabs.pricing",
    icon: DollarSign,
    path: "/admin/pricing",
  },
  {
    id: "admin-licences",
    label: "admin.tabs.licences",
    icon: CreditCard,
    path: "/admin/licences",
  },
  {
    id: "admin-licence-promotions",
    label: "admin.tabs.licencePromotions",
    icon: Percent,
    path: "/admin/licence-promotions",
  },
  {
    id: "admin-governance",
    label: "admin.tabs.governance",
    icon: Shield,
    children: [
      {
        id: "admin-user-governance",
        label: "admin.tabs.userGovernance",
        icon: Shield,
        path: "/admin/user-governance",
      },
      {
        id: "admin-app-config-announcement",
        label: "admin.tabs.announcement",
        icon: Megaphone,
        path: "/admin/governance/announcement",
      },
      {
        id: "admin-app-config-maintenance",
        label: "admin.tabs.maintenance",
        icon: Wrench,
        path: "/admin/governance/maintenance",
      },
      {
        id: "admin-feature-flags",
        label: "admin.tabs.featureFlags",
        icon: ToggleLeft,
        path: "/admin/governance/feature-flags",
      },
    ],
  },
];

export const Sidebar: React.FC<SidebarProps> = ({
  onClose,
  isMobile = false,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  // MED-1543: this line was the constant "admin.role", so it called a
  // SUPER_ADMIN "Administrateur" while the header two inches away read
  // "Super administrateur". Same session user and the same label set the
  // header uses (MED-1514), so the two agree.
  const sessionRole = useAppSelector(
    state => state.session.session?.user?.roleName
  );
  const [collapsed, setCollapsed] = useState(false);

  // Use admin menu items for all users
  const menuItems = adminMenuItems;

  // Helper function to safely translate
  const safeTranslate = (key: string) => {
    if (!i18n.isInitialized) {
      return key; // Return key if i18n is not ready
    }
    const translation = t(key);
    return translation === key ? key : translation;
  };

  const isActive = (path: string) => {
    // For the overview page (/admin), only match exact path or /admin/
    if (path === "/admin") {
      return location.pathname === "/admin" || location.pathname === "/admin/";
    }

    // For other pages, match exact path or paths that start with the path + "/"
    return (
      location.pathname === path || location.pathname.startsWith(path + "/")
    );
  };

  // Tracks which group menus are open. Defaults to open when the current
  // location lives under one of the group's children so the active leaf is
  // visible on mount/refresh.
  const initialOpenGroups: Record<string, boolean> = React.useMemo(() => {
    const out: Record<string, boolean> = {};
    for (const entry of adminMenuItems) {
      if (isGroup(entry)) {
        out[entry.id] = entry.children.some(child => isActive(child.path));
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const [openGroups, setOpenGroups] =
    useState<Record<string, boolean>>(initialOpenGroups);

  // Keep groups open when location changes into one of their children.
  React.useEffect(() => {
    setOpenGroups(prev => {
      const next = { ...prev };
      for (const entry of adminMenuItems) {
        if (
          isGroup(entry) &&
          entry.children.some(child => isActive(child.path))
        ) {
          next[entry.id] = true;
        }
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleGroup = (id: string) => {
    setOpenGroups(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleLogout = () => {
    // Dispatch terminate session action - saga handles API call and redirect
    dispatch(requestTerminateSessionRequest());
  };

  const handleLinkClick = () => {
    if (isMobile && onClose) {
      onClose(); // Close sidebar on mobile after navigation
    }
    scrollToTop("smooth"); // Scroll to top after navigation
  };

  return (
    <div
      className={`bg-white border-r border-neutral-200 transition-all duration-300 flex flex-col h-full ${
        collapsed ? "w-16" : "w-64"
      } ${isMobile ? "w-64" : ""}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between flex-shrink-0 p-4 border-b border-neutral-200">
        {(!collapsed || isMobile) && (
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-ews-primary to-secondary-500">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-neutral-900">
                Medicine360
              </h1>
              <p className="text-xs text-neutral-500">
                {roleLabel(t, sessionRole) || safeTranslate("admin.role")}
              </p>
            </div>
          </div>
        )}
        {!isMobile && (
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-1 transition-colors rounded-md hover:bg-neutral-100"
          >
            <ChevronDown
              className={`w-4 h-4 text-neutral-600 transition-transform ${
                collapsed ? "rotate-90" : ""
              }`}
            />
          </button>
        )}
        {isMobile && onClose && (
          <button
            onClick={onClose}
            className="p-1 transition-colors rounded-md hover:bg-neutral-100"
          >
            <ChevronDown className="w-4 h-4 text-neutral-600" />
          </button>
        )}
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
        {menuItems.map(item => {
          if (isGroup(item)) {
            const isOpen = openGroups[item.id] ?? false;
            const groupActive = item.children.some(child =>
              isActive(child.path)
            );
            return (
              <div key={item.id}>
                <button
                  type="button"
                  onClick={() => toggleGroup(item.id)}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium sidebar-item ${
                    groupActive
                      ? "sidebar-item-active"
                      : "text-neutral-700 sidebar-item-hover"
                  }`}
                  aria-expanded={isOpen}
                  aria-controls={`${item.id}-submenu`}
                >
                  <div className="flex items-center space-x-3">
                    <item.icon
                      className={`w-5 h-5 sidebar-item-icon ${
                        groupActive
                          ? "sidebar-item-icon-active"
                          : "sidebar-item-icon-inactive"
                      }`}
                    />
                    {(!collapsed || isMobile) && (
                      <span>{safeTranslate(item.label)}</span>
                    )}
                  </div>
                  {(!collapsed || isMobile) && (
                    <ChevronRight
                      className={`w-4 h-4 text-neutral-500 transition-transform ${
                        isOpen ? "rotate-90" : ""
                      }`}
                    />
                  )}
                </button>
                {isOpen && (!collapsed || isMobile) && (
                  <div
                    id={`${item.id}-submenu`}
                    className="pl-2 mt-1 ml-6 space-y-1 border-l border-neutral-200"
                  >
                    {item.children.map(child => (
                      <Link
                        key={child.id}
                        to={child.path}
                        onClick={handleLinkClick}
                        className={`w-full flex items-center px-3 py-2 rounded-lg text-sm font-medium sidebar-item ${
                          isActive(child.path)
                            ? "sidebar-item-active"
                            : "text-neutral-700 sidebar-item-hover"
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <child.icon
                            className={`w-4 h-4 sidebar-item-icon ${
                              isActive(child.path)
                                ? "sidebar-item-icon-active"
                                : "sidebar-item-icon-inactive"
                            }`}
                          />
                          <span>{safeTranslate(child.label)}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          }

          return (
            <Link
              key={item.id}
              to={item.path}
              onClick={handleLinkClick}
              className={`w-full flex items-center px-3 py-2 rounded-lg text-sm font-medium sidebar-item ${
                isActive(item.path)
                  ? "sidebar-item-active"
                  : "text-neutral-700 sidebar-item-hover"
              } ${item.id === "staff" ? "mt-4" : ""}`}
            >
              <div className="flex items-center space-x-3">
                <item.icon
                  className={`w-5 h-5 sidebar-item-icon ${
                    isActive(item.path)
                      ? "sidebar-item-icon-active"
                      : "sidebar-item-icon-inactive"
                  }`}
                />
                {(!collapsed || isMobile) && (
                  <span>{safeTranslate(item.label)}</span>
                )}
              </div>
            </Link>
          );
        })}
      </nav>

      {/* Bottom Actions - Fixed at bottom */}
      <div className="flex-shrink-0 p-4 space-y-2 border-t border-neutral-200">
        {(!collapsed || isMobile) && (
          <>
            <LanguageSwitcher />
            <button
              onClick={handleLinkClick}
              className="flex items-center w-full px-3 py-2 space-x-3 text-sm transition-colors rounded-lg text-neutral-700 hover:bg-neutral-50 hover:text-neutral-900"
            >
              <HelpCircle className="w-5 h-5 text-neutral-500" />
              <span>{safeTranslate("sidebar.helpSupport")}</span>
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center w-full px-3 py-2 space-x-3 text-sm transition-colors rounded-lg text-danger-600 hover:bg-danger-50"
            >
              <LogOut className="w-5 h-5" />
              <span>{safeTranslate("sidebar.logout")}</span>
            </button>
          </>
        )}
        {collapsed && !isMobile && (
          <div className="flex flex-col items-center space-y-2">
            <button
              onClick={handleLinkClick}
              className="p-2 transition-colors rounded-lg text-neutral-700 hover:bg-neutral-50"
            >
              <HelpCircle className="w-5 h-5 text-neutral-500" />
            </button>
            <button
              onClick={handleLogout}
              className="p-2 transition-colors rounded-lg text-danger-600 hover:bg-danger-50"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
